/**
 * Which sellers' first-time banners this buyer has dismissed.
 *
 * Per seller, not one flag for all of them: the banner's content is built from
 * each storefront's own config, so dismissing one seller's says nothing about
 * another's. Scoped to the buyer too, so switching accounts in a tab cannot
 * inherit the previous buyer's dismissals.
 *
 * Session-scoped, and cleared on sign-out by {@link clearBannerDismissals} —
 * `sessionStorage` outlives a sign-out in the same tab, which would otherwise
 * keep the banner hidden from the next login.
 */
const PREFIX = 'bstock.sellerCollectsBanner.dismissed'

const keyFor = (accountId: string | undefined, sellerAccountId: string) =>
  `${PREFIX}.${accountId ?? 'anonymous'}.${sellerAccountId}`

export const isBannerDismissed = (
  accountId: string | undefined,
  sellerAccountId: string | undefined
): boolean => {
  if (sellerAccountId === undefined) return false
  try {
    return sessionStorage.getItem(keyFor(accountId, sellerAccountId)) === 'true'
  } catch {
    // Private browsing or blocked storage — the banner simply stays.
    return false
  }
}

export const dismissBanner = (
  accountId: string | undefined,
  sellerAccountId: string | undefined
): void => {
  if (sellerAccountId === undefined) return
  try {
    sessionStorage.setItem(keyFor(accountId, sellerAccountId), 'true')
  } catch {
    // Dismissal then lasts only as long as this page view.
  }
}

/** Called on sign-out, so the next login starts the banners over. */
export const clearBannerDismissals = (): void => {
  try {
    const stale = Object.keys(sessionStorage).filter((key) =>
      key.startsWith(PREFIX)
    )
    stale.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
